import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import { UserContext } from "@redotech/redo-merchant-app-common/user";
import {
  MAX_MAX_QUESTIONS,
  MIN_MAX_QUESTIONS,
} from "@redotech/redo-model/agents/returns-agents/util/return-reason-conversation-util";
import { SettingsPermissions } from "@redotech/redo-model/permissions";
import {
  AiReturnReasonsState,
  CustomGuidanceState,
} from "@redotech/redo-return-ui/flows/return-flow/ai-return-reasons";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoCollapseSubsection } from "@redotech/redo-web/arbiter-components/collapse/redo-collapse-section";
import { RedoTextInput } from "@redotech/redo-web/arbiter-components/input/redo-text-input";
import { RedoTextareaInputField } from "@redotech/redo-web/arbiter-components/input/redo-textarea-input-field";
import { RedoExternalLink } from "@redotech/redo-web/arbiter-components/links/redo-external-link";
import {
  ButtonSize,
  IconButton,
  IconButtonTheme,
} from "@redotech/redo-web/button";
import { Divider } from "@redotech/redo-web/divider";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { produce } from "immer";
import {
  Dispatch,
  memo,
  ReactNode,
  SetStateAction,
  useContext,
  useMemo,
} from "react";
import { AdditionalAiInfo } from "./multiple-choice";
import * as multipleChoiceCss from "./multiple-choice.module.css";

type StepSelectFn = StepTypeDetailsProps<AiReturnReasonsState>["stepSelect"];

export const AiReturnReasonsDetails = memo(function AiReturnReasonsDetails({
  stepSelect,
  state,
  setState,
}: StepTypeDetailsProps<AiReturnReasonsState>) {
  const user = useContext(UserContext);
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const isRedoUser =
    (user?.email && /@getredo\.com/.test(user?.email)) || false;

  const automationsLink = useMemo(
    () => `${window.location.href.split("/settings")[0]}/settings/automations`,
    [],
  );

  const nextStepChange = useHandler((next?: StepId) => {
    setState((state) => ({ ...state, next }));
  });

  const nextWrongProductStepChange = useHandler((nextWrongProduct?: StepId) => {
    setState((state) => ({ ...state, nextWrongProduct }));
  });

  const nextDamagedStepChange = useHandler((nextDamaged?: StepId) => {
    setState((state) => ({ ...state, nextDamaged }));
  });

  const handleAdditionalAiInfo = useHandler(
    (additionalAiInfo: string | undefined) =>
      setState((state) => ({ ...state, additionalAiInfo })),
  );

  const handleMaxQuestionsChange = useHandler(
    (maxQuestions: number | undefined) => {
      setState((state) => ({ ...state, maxQuestions }));
    },
  );

  const handleGuidanceChange = useHandler(
    (merchantGuidance: string | undefined) => {
      setState((state) => ({
        ...state,
        merchantGuidance: merchantGuidance || undefined,
      }));
    },
  );

  const handleCustomQuestionChange = useHandler(
    (customQuestion: string | undefined) => {
      setState((state) => ({
        ...state,
        customQuestion: customQuestion || undefined,
      }));
    },
  );

  const handleCustomExplanationSubtextChange = useHandler(
    (customExplanationSubtext: string | undefined) => {
      setState((state) => ({
        ...state,
        customExplanationSubtext: customExplanationSubtext || undefined,
      }));
    },
  );

  const handleAddCustomGuidance = useHandler(() =>
    setState((state) =>
      produce(state, (draft) => {
        if (!draft.customGuidance) {
          draft.customGuidance = [];
        }
        draft.customGuidance.push({
          id: crypto.randomUUID(),
          description: "",
          next: undefined,
        });
      }),
    ),
  );

  function getMaxQuestionsValidationErrors(maxQuestions: number | undefined) {
    if (maxQuestions === undefined) {
      return [];
    }
    if (maxQuestions < MIN_MAX_QUESTIONS) {
      return [`Must be greater than ${MIN_MAX_QUESTIONS - 1}`];
    }
    if (maxQuestions > MAX_MAX_QUESTIONS) {
      return [`Must be less than or equal to ${MAX_MAX_QUESTIONS}`];
    }
    return [];
  }

  const customGuidance = state.customGuidance ?? [];

  return (
    <>
      <Flex dir="column" gap="xl">
        <Flex dir="column" gap="xs">
          <Text fontSize="sm" fontWeight="semibold" textColor="primary">
            Guidance
          </Text>
          <Text fontSize="xxs" textColor="tertiary">
            Define how the AI should route customers based on their return
            reason. The first two rules are built-in.
          </Text>
        </Flex>

        <BuiltInGuidanceCard
          handleIndex={0}
          ifText="Product is damaged"
          required
          requiredMissing={!state.nextDamaged}
          stepSelect={stepSelect}
          value={state.nextDamaged}
          valueChange={nextDamagedStepChange}
        />
        <BuiltInGuidanceCard
          handleIndex={1}
          ifText="Wrong product"
          required
          requiredMissing={!state.nextWrongProduct}
          stepSelect={stepSelect}
          value={state.nextWrongProduct}
          valueChange={nextWrongProductStepChange}
        />

        <IterableMap items={customGuidance} keyFn={(g) => g.id}>
          {(guidance, index) => (
            <CustomGuidanceCardComponent
              canEditReturnsSettings={canEditReturnsSettings}
              guidance={guidance}
              index={index}
              setState={setState}
              stepSelect={stepSelect}
            />
          )}
        </IterableMap>

        <Flex align="flex-start">
          <RedoButton
            disabled={!canEditReturnsSettings}
            hierarchy="secondary"
            IconLeading={PlusIcon}
            onClick={handleAddCustomGuidance}
            size="sm"
            text="Add guidance"
          />
        </Flex>

        <Divider />

        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            All other cases go...
          </Text>
          {stepSelect({
            value: state.next,
            valueChange: nextStepChange,
            handleIndex: 2 + customGuidance.length,
          })}
          {!state.next && (
            <Text fontSize="xs" fontWeight="regular" textColor="error">
              Required
            </Text>
          )}
        </Flex>

        <Divider />

        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Custom question (optional)
          </Text>
          <RedoTextareaInputField
            placeholder="Why are you returning this item?"
            readOnly={!canEditReturnsSettings}
            setValue={handleCustomQuestionChange}
            value={state.customQuestion || ""}
          />
          <Text fontSize="xxs" textColor="tertiary">
            The initial question shown to customers. Leave empty to use the
            default question. Make sure to not bias the customer with a leading
            question to ensure data is as accurate as possible.
          </Text>
        </Flex>
        <Divider />
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Custom explanation subtext (optional)
          </Text>
          <RedoTextareaInputField
            placeholder="Please explain in detail why you are returning this item."
            readOnly={!canEditReturnsSettings}
            setValue={handleCustomExplanationSubtextChange}
            value={state.customExplanationSubtext || ""}
          />
          <Text fontSize="xxs" textColor="tertiary">
            The subtext shown below the main question. Leave empty to use the
            default subtext. Make sure to not bias the customer with a leading
            question to ensure data is as accurate as possible.
          </Text>
        </Flex>
      </Flex>
      <Divider />

      <RedoCollapseSubsection title="Advanced options">
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Predefined categories
          </Text>
          <Text fontSize="xxs" textColor="tertiary">
            Optionally define categories for the AI to sort return reasons into.
          </Text>
          <RedoExternalLink
            fontSize="md"
            text="Manage predefined categories"
            url={automationsLink}
          />
        </Flex>
        <Divider />
        <Text fontSize="xs" textColor="secondary">
          Maximum questions
        </Text>
        {getMaxQuestionsValidationErrors(state.maxQuestions).map(
          (error, index) => (
            <Text
              fontSize="xs"
              fontWeight="regular"
              key={index}
              textColor="error"
            >
              {error}
            </Text>
          ),
        )}

        <RedoTextInput
          max={MAX_MAX_QUESTIONS}
          min={MIN_MAX_QUESTIONS}
          placeholder="2"
          setValue={(value: string) => {
            const numValue = parseInt(value, 10);
            if (!isNaN(numValue)) {
              handleMaxQuestionsChange(numValue);
            } else {
              handleMaxQuestionsChange(undefined);
            }
          }}
          state={canEditReturnsSettings ? "default" : "readonly"}
          step={1}
          value={String(state.maxQuestions ?? "")}
        />
        <Text fontSize="xxs" textColor="tertiary">
          {`Maximum number of questions the AI can ask the customer (default: 2, min: ${MIN_MAX_QUESTIONS}, max: ${MAX_MAX_QUESTIONS}). Set to 1 to categorize after a single question with no follow-ups.`}
        </Text>
        <Text fontSize="xs" textColor="secondary">
          Guidance
        </Text>
        <RedoTextareaInputField
          readOnly={!canEditReturnsSettings}
          setValue={handleGuidanceChange}
          value={state.merchantGuidance || ""}
        />
        <Text fontSize="xxs" textColor="tertiary">
          Additional instructions for the AI to follow
        </Text>
      </RedoCollapseSubsection>
      {isRedoUser ? (
        <AdditionalAiInfo
          canEditReturnsSettings={canEditReturnsSettings}
          collapsedTitle="Redo user advanced options"
          currentAdditionalAiInfo={state.additionalAiInfo}
          setAdditionalAiInfo={handleAdditionalAiInfo}
        />
      ) : null}
    </>
  );
});

const BuiltInGuidanceCard = memo(function BuiltInGuidanceCard({
  ifText,
  stepSelect,
  value,
  valueChange,
  handleIndex,
  required,
  requiredMissing,
}: {
  ifText: string;
  stepSelect: StepSelectFn;
  value: StepId | undefined;
  valueChange: (next: StepId | undefined) => void;
  handleIndex: number;
  required?: boolean;
  requiredMissing?: boolean;
}): ReactNode {
  return (
    <Flex className={multipleChoiceCss.optionCard} dir="column" gap="3xl">
      <Flex dir="column" gap="none">
        <Text fontSize="xs" textColor="secondary">
          If...
        </Text>
        <RedoTextInput setValue={() => {}} state="readonly" value={ifText} />
      </Flex>
      <Flex dir="column" gap="xs">
        <Text fontSize="xs" textColor="secondary">
          Then...
        </Text>
        {stepSelect({ value, valueChange, handleIndex })}
        {required && requiredMissing && (
          <Text fontSize="xs" fontWeight="regular" textColor="error">
            Required
          </Text>
        )}
      </Flex>
    </Flex>
  );
});

const CustomGuidanceCardComponent = memo(function CustomGuidanceCardComponent({
  canEditReturnsSettings,
  guidance,
  index,
  setState,
  stepSelect,
}: {
  canEditReturnsSettings: boolean;
  guidance: CustomGuidanceState;
  index: number;
  setState: Dispatch<SetStateAction<AiReturnReasonsState>>;
  stepSelect: StepSelectFn;
}) {
  const setDescription = useHandler((description: string) =>
    setState((s) =>
      produce(s, (draft) => {
        if (draft.customGuidance?.[index]) {
          draft.customGuidance[index]!.description = description;
        }
      }),
    ),
  );
  const setNext = useHandler((next: StepId | undefined) =>
    setState((s) =>
      produce(s, (draft) => {
        if (draft.customGuidance?.[index]) {
          draft.customGuidance[index]!.next = next;
        }
      }),
    ),
  );
  const handleDelete = useHandler(() =>
    setState((s) =>
      produce(s, (draft) => {
        draft.customGuidance?.splice(index, 1);
      }),
    ),
  );

  return (
    <Flex className={multipleChoiceCss.optionCard} dir="column" gap="md">
      <Flex align="center" justify="space-between">
        <Text fontSize="sm" fontWeight="semibold" textColor="primary">
          Custom guidance
        </Text>
        {canEditReturnsSettings && (
          <IconButton
            onClick={handleDelete}
            size={ButtonSize.SMALL}
            theme={IconButtonTheme.DANGER}
          >
            <TrashIcon />
          </IconButton>
        )}
      </Flex>
      <Flex dir="column" gap="3xl">
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            If...
          </Text>
          <RedoTextInput
            placeholder="e.g. The product doesn't fit"
            setValue={setDescription}
            state={!canEditReturnsSettings ? "readonly" : undefined}
            value={guidance.description}
          />
        </Flex>
        <Flex dir="column" gap="xs">
          <Text fontSize="xs" textColor="secondary">
            Then...
          </Text>
          {stepSelect({
            value: guidance.next,
            valueChange: setNext,
            handleIndex: 2 + index,
          })}
          {!guidance.next && (
            <Text fontSize="xs" fontWeight="regular" textColor="error">
              Required
            </Text>
          )}
        </Flex>
      </Flex>
    </Flex>
  );
});
